import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { bakEvents } from '../data/bakEvents';
import { experts } from '../data/experts';
import type { BakEvent, Invitation, InvitationStatus, InviteInput, NewEventInput } from '../types/event';
type EventsContextValue = {
  events: BakEvent[];
  createEvent: (input: NewEventInput) => string;
  inviteExperts: (eventId: string, input: InviteInput) => void;
  remindExpert: (eventId: string, invitationId: string) => void;
  withdrawInvitation: (eventId: string, invitationId: string) => void;
};
const EventsContext = createContext<EventsContextValue | null>(null);
export function EventsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [events, setEvents] = useState<BakEvent[]>(bakEvents);
  const timers = useRef<number[]>([]);
  useEffect(() => {
    const list = timers.current;
    return () => list.forEach(t => window.clearTimeout(t));
  }, []);
  const schedule = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  };
  const setStatus = useCallback((eventId: string, invitationId: string, status: InvitationStatus, note?: string) => {
    setEvents(prev => prev.map(ev => ev.id !== eventId ? ev : {
      ...ev,
      invitations: ev.invitations.map(inv => inv.id !== invitationId ? inv : {
        ...inv,
        status,
        responseNote: note ?? inv.responseNote,
        respondedAt: status === 'Accepted' || status === 'Declined' ? new Date().toISOString() : inv.respondedAt
      })
    }));
  }, []);
  const simulateResponse = useCallback((eventId: string, eventName: string, invitation: Invitation) => {
    const expert = experts.find(e => e.id === invitation.expertId);
    if (!expert) return;
    const acceptChance = expert.availability === 'Booked' ? 0.15 : expert.availability === 'Limited' ? 0.6 : 0.85;
    const accepts = Math.random() < acceptChance;
    const viewDelay = 2500 + Math.random() * 2500;
    const respondDelay = viewDelay + 2500 + Math.random() * 4000;
    schedule(() => setStatus(eventId, invitation.id, 'Delivered'), 1000);
    schedule(() => setStatus(eventId, invitation.id, 'Viewed'), viewDelay);
    schedule(() => {
      if (accepts) {
        setStatus(eventId, invitation.id, 'Accepted', 'Happy to participate — looking forward to it.');
        toast.success(`${expert.name} accepted`, {
          description: `${invitation.role} · ${eventName}`
        });
      } else {
        setStatus(eventId, invitation.id, 'Declined', 'Unable to attend due to a prior commitment.');
        toast.error(`${expert.name} declined`, {
          description: eventName
        });
      }
    }, respondDelay);
  }, [setStatus]);
  const createEvent = useCallback((input: NewEventInput) => {
    const id = `${input.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}-${Date.now().toString(36)}`;
    setEvents(prev => [{
      ...input,
      id,
      invitations: []
    }, ...prev]);
    toast.success('Event created', {
      description: input.name
    });
    return id;
  }, []);
  const inviteExperts = useCallback((eventId: string, input: InviteInput) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    const now = new Date().toISOString();
    const newInvites: Invitation[] = input.expertIds.map(expertId => {
      const expert = experts.find(e => e.id === expertId);
      const fee = input.role === 'Keynote speaker' || input.role === 'Advisor' || input.role === 'Facilitator' ? expert?.dailyRate ?? 0 : expert?.halfDayRate ?? 0;
      return {
        id: `${expertId}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
        expertId,
        role: input.role,
        proposedFee: fee,
        sentAt: now,
        status: 'Sending'
      };
    });
    setEvents(prev => prev.map(ev => ev.id === eventId ? {
      ...ev,
      invitations: [...ev.invitations, ...newInvites]
    } : ev));
    toast.info(`Notifying ${newInvites.length} expert${newInvites.length > 1 ? 's' : ''}`, {
      description: 'Sent via email, SMS and the BAK expert app.'
    });
    newInvites.forEach(inv => simulateResponse(eventId, event.name, inv));
  }, [events, simulateResponse]);
  const remindExpert = useCallback((eventId: string, invitationId: string) => {
    const event = events.find(e => e.id === eventId);
    const inv = event?.invitations.find(i => i.id === invitationId);
    const expert = experts.find(e => e.id === inv?.expertId);
    if (!event || !inv || !expert) return;
    toast.info(`Reminder sent to ${expert.name}`);
    simulateResponse(eventId, event.name, inv);
  }, [events, simulateResponse]);
  const withdrawInvitation = useCallback((eventId: string, invitationId: string) => {
    setEvents(prev => prev.map(ev => ev.id === eventId ? {
      ...ev,
      invitations: ev.invitations.filter(i => i.id !== invitationId)
    } : ev));
    toast('Invitation withdrawn');
  }, []);
  return <EventsContext.Provider value={{
    events,
    createEvent,
    inviteExperts,
    remindExpert,
    withdrawInvitation
  }} data-id="element-665" data-mp="3tmfowjeiqlrp">
      {children}
    </EventsContext.Provider>;
}
export function useEvents(): EventsContextValue {
  const ctx = useContext(EventsContext);
  if (!ctx) throw new Error('useEvents must be used within EventsProvider');
  return ctx;
}